<template>
  <div class="company-information-container" v-if="bankDetails">
    <div class="d-flex align-items-center">
      <div class="d-flex flex-column flex-md-row align-items-center">
        <div class="drip-h2 color-noble-blue-500">
          {{ capitalizeFirstLetter(bankDetails.bank_name) }}
        </div>
        <chip
          :class="
            'ml-0 ml-md-3 bank-status ' + getAccountStatus(bankDetails.status)
          "
          :chipText="getAccountStatus(bankDetails.status)"
        />
      </div>
      <div class="edit-button">
        <action-button
          v-if="hasPermissionToUpdateBank && !isVerified"
          variant="outline"
          size="small"
          :click="() => editBankDetails()"
          extraClass="resizeButton"
        >
          <b-icon-pencil scale="1" class="mr-2" />
          <div class="drip-body">Edit</div>
        </action-button>
        <action-button
          v-else-if="!hasPermissionToUpdateBank && !isVerified"
          :bTitle="$t('error.unauthorizedToolTip')"
          variant="outline"
          size="small"
          extraClass="resizeButton"
        >
          <b-icon-pencil scale="1" class="mr-2" />
          <div class="drip-body">Edit</div>
        </action-button>
      </div>
    </div>
    <template>
      <div class="spacing-24"></div>
      <div class="drip-h3 font-weight-400 color-noble-blue-500">
        {{ $t('bank.view.company.title') }}
      </div>
      <div class="spacing-12"></div>
      <div class="d-flex">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.name') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.beneficiary_name }}
        </div>
      </div>
      <div class="spacing-16"></div>
      <div class="d-flex">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.bank_name') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.bank_name }}
        </div>
      </div>
      <div class="spacing-16"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.bank_city') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.bank_city }}
        </div>
      </div>
      <div class="spacing-16"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.account_number') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.account_number }}
        </div>
      </div>
      <div class="spacing-16"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.swift_code') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.swift_code }}
        </div>
      </div>
      <div class="spacing-16"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.currency') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.currency && bankDetails.currency.toUpperCase() }}
        </div>
      </div>
      <div class="spacing-24"></div>
      <div class="drip-h3 color-noble-blue-500 font-weight-400">
        {{ $t('bank.view.company.documentsTitle') }}
      </div>
      <div class="spacing-2"></div>
      <div class="drip-body color-noble-blue-500">
        <span class="font-weight-400">
          {{ $t('bank.addBank.form.supporting_document.title') }}
        </span>
        <br />
        {{ $t('bank.addBank.form.supporting_document.subtitle') }}
      </div>
      <div class="spacing-8"></div>
      <template>
        <view-uploads
          :title="$t('bank.view.company.pci.title')"
          :subtitle="$t('bank.view.company.pci.subtext')"
          :documents="getSupportingDocuments"
        ></view-uploads>
        <div class="spacing-24"></div>
      </template>
      <div class="drip-h3 color-noble-blue-500 font-weight-400">
        {{ $t('bank.addBank.form.correspondingDetails.title') }}
      </div>
      <div class="spacing-2"></div>
      <div class="drip-body color-noble-blue-500 font-weight-700">
        {{ $t('bank.addBank.form.correspondingDetails.subtext') }}
      </div>
      <div class="spacing-12"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.corresponding_name') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.correspondent_bank_name }}
        </div>
      </div>
      <div class="spacing-16"></div>
      <div class="d-flex color-noble-blue-500">
        <div class="drip-body width-200 color-noble-blue-500">
          {{ $t('bank.view.company.corresponding_swift_code') }}
        </div>
        <div class="drip-body font-weight-400 color-black">
          {{ bankDetails.correspondent_swift_code }}
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import ActionButton from '@/components/common/ActionButton'
import Chip from '@/components/common/Chip'
import ViewUploads from './ViewUploads'
import { ACTIONS, ENTITIES } from '@/utils/constants'
import { capitalizeFirstLetter } from '@/utils/common'

export default {
  components: {
    ViewUploads,
    ActionButton,
    Chip
  },
  props: ['bankDetails', 'supporting_documents'],
  data() {
    return {
      documents: [],
      bank_supporting_document: []
    }
  },
  methods: {
    capitalizeFirstLetter(str) {
      return capitalizeFirstLetter(str)
    },
    getAccountStatus(status) {
      return status === 'verified' ? 'Verified' : 'Verification Pending'
    },
    editBankDetails() {
      this.$mpTrack(`Bank details - Navigate to edit bank form`, {
        bankId: this.bankDetails.id
      })
      this.$router.push({
        name: 'addBankAccount',
        params: {
          bid: this.bankDetails.id
        }
      })
    }
  },
  computed: {
    hasPermissionToUpdateBank() {
      return this.$store.getters.hasActionPermission(
        ENTITIES.BANK,
        ACTIONS.CREATE
      )
    },
    isVerified() {
      return this.bankDetails.status === 'verified'
    },
    getSupportingDocuments() {
      return this.supporting_documents.filter(
        (x) => x.tags === 'bank_supporting_document'
      )
    }
  },
  watch: {
    supporting_documents: {
      handler: function () {
        if (this.supporting_documents && this.supporting_documents.length) {
          this.documents = this.supporting_documents
          this.bank_supporting_document = this.documents.filter(
            (x) => x.tags === 'bank_supporting_document'
          )
        }
      },
      deep: true
    }
  }
}
</script>
<style lang="scss" scoped>
.company-information-container {
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: $sky-blue-400;
  margin-bottom: 48px;
  .width-200 {
    width: 200px;
    min-width: 200px;
    @media screen and (max-width: 576px) {
      width: 140px;
      min-width: 140px;
      padding-right: 10px;
    }
  }
  .edit-button {
    margin-left: auto;
    .resizeButton {
      height: 40px;
    }
  }
}
</style>
